/**
 * Automatic widget placement for mosaic layout.
 *
 * When adding a new widget, determines optimal placement by:
 * 1. Preferring free space - uses maximal free rectangles if available
 * 2. Falling back to splitting - adds to row/column (N-way) OR splits widget (2-way)
 * 3. Optimizing for squareness - primary factor in scoring placement options
 * 4. Horizontal bias - side-by-side slightly preferred over stacked (5% bias)
 */
import { collectGridLines, buildOccupancyGrid, findMaximalFreeRectangles } from './mosaic-drag-constraints';
import type { FreeRect } from './mosaic-drag-constraints';
import { MIN_SIZE } from './mosaic-drop-targets';
import type { NormalizedLayout } from './mosaic-drop-targets';
import { computeLayoutInferenceState } from './mosaic-layout-inference';
import type { InferredColumn, InferredRow } from './mosaic-layout-inference';
import { container } from 'src/StaticContainer';

const log = container.get('Logger').getSubLogger({ name: 'mosaic-widget-placement' });

/** Maximum gap (normalized coords) that gap-fill will close. */
export const MOSAIC_GROW_THRESHOLD = 0.1;

export interface WidgetSize {
    w: number;
    h: number;
}

export interface PlacementResult {
    type: 'free-space' | 'split' | 'row-insert' | 'column-insert';
    layout: NormalizedLayout;
    affectedLayouts?: Record<string, NormalizedLayout>;
}

export interface PlacementOptions {
    /** Multiplier applied to horizontal operations for bias. Default: 0.95 */
    preferHorizontalBias?: number;
    /** Minimum widget size (fraction of viewport). Default: 0.05 */
    minSize?: number;
}

const DEFAULT_HORIZONTAL_BIAS = 0.95;
const DEFAULT_MIN_SIZE = MIN_SIZE;

/** Epsilon tolerance for floating-point edge-sharing comparisons. */
const OVERLAP_EPSILON = 1e-9;

/** Check whether two ranges [aStart, aEnd] and [bStart, bEnd] overlap on the vertical axis. */
function overlapsVertically(a: NormalizedLayout, b: NormalizedLayout): boolean {
    return a.y < b.y + b.h - OVERLAP_EPSILON && b.y < a.y + a.h - OVERLAP_EPSILON;
}

/** Check whether two ranges [aStart, aEnd] and [bStart, bEnd] overlap on the horizontal axis. */
function overlapsHorizontally(a: NormalizedLayout, b: NormalizedLayout): boolean {
    return a.x < b.x + b.w - OVERLAP_EPSILON && b.x < a.x + a.w - OVERLAP_EPSILON;
}

/**
 * Grow a layout to fill small gaps to the nearest obstacle on each side.
 * For each of the 4 sides, finds the nearest obstacle (neighbor edge or canvas boundary 0/1)
 * that overlaps on the perpendicular axis. If the gap is > 0 and <= threshold, extends
 * the widget to close it.
 *
 * Processes left/right first, then top/bottom (so vertical checks use the post-grow horizontal span).
 *
 * @param layout - The layout to grow (not mutated)
 * @param otherLayouts - Neighboring widget layouts
 * @param threshold - Maximum gap size to close (default: MOSAIC_GROW_THRESHOLD)
 * @returns A new layout with gaps filled where applicable
 */
export function growLayoutToFillGaps(
    layout: NormalizedLayout,
    otherLayouts: NormalizedLayout[],
    threshold: number = MOSAIC_GROW_THRESHOLD,
): NormalizedLayout {
    let { x, y, w, h } = layout;

    // --- Left side ---
    {
        // Nearest obstacle to the left: either canvas edge (0) or right edge of a vertically-overlapping neighbor
        let nearestLeft = 0; // canvas left boundary
        for (const other of otherLayouts) {
            const otherRight = other.x + other.w;
            // Neighbor must be to the left and overlap vertically
            if (otherRight <= x + OVERLAP_EPSILON && overlapsVertically({ x, y, w, h }, other)) {
                nearestLeft = Math.max(nearestLeft, otherRight);
            }
        }
        const gap = x - nearestLeft;
        if (gap > OVERLAP_EPSILON && gap <= threshold) {
            w += gap;
            x = nearestLeft;
        }
    }

    // --- Right side ---
    {
        let nearestRight = 1; // canvas right boundary
        for (const other of otherLayouts) {
            // Neighbor must be to the right and overlap vertically
            if (other.x >= x + w - OVERLAP_EPSILON && overlapsVertically({ x, y, w, h }, other)) {
                nearestRight = Math.min(nearestRight, other.x);
            }
        }
        const gap = nearestRight - (x + w);
        if (gap > OVERLAP_EPSILON && gap <= threshold) {
            w += gap;
        }
    }

    // --- Top side (uses post-grow horizontal span) ---
    {
        let nearestTop = 0; // canvas top boundary
        for (const other of otherLayouts) {
            const otherBottom = other.y + other.h;
            // Neighbor must be above and overlap horizontally (using current x, w)
            if (otherBottom <= y + OVERLAP_EPSILON && overlapsHorizontally({ x, y, w, h }, other)) {
                nearestTop = Math.max(nearestTop, otherBottom);
            }
        }
        const gap = y - nearestTop;
        if (gap > OVERLAP_EPSILON && gap <= threshold) {
            h += gap;
            y = nearestTop;
        }
    }

    // --- Bottom side (uses post-grow horizontal span) ---
    {
        let nearestBottom = 1; // canvas bottom boundary
        for (const other of otherLayouts) {
            // Neighbor must be below and overlap horizontally (using current x, w)
            if (other.y >= y + h - OVERLAP_EPSILON && overlapsHorizontally({ x, y, w, h }, other)) {
                nearestBottom = Math.min(nearestBottom, other.y);
            }
        }
        const gap = nearestBottom - (y + h);
        if (gap > OVERLAP_EPSILON && gap <= threshold) {
            h += gap;
        }
    }

    return { x, y, w, h };
}

/**
 * Compute squareness score. Lower is better.
 * 1.0 = perfect square, higher = more elongated (worse).
 */
export function computeSquarenessScore(w: number, h: number): number {
    if (w === 0 || h === 0) return Infinity;
    return Math.max(w / h, h / w);
}

/**
 * Score a free rectangle for placing a widget.
 * Lower score is better.
 *
 * When widgetSize is provided, includes a squareness penalty for the resulting widget.
 * Without widgetSize, scores purely on position (top-left bias) and area.
 */
export function scoreFreeRect(rect: FreeRect, widgetSize?: WidgetSize): number {
    // Squareness of the resulting widget (placed at its natural size)
    const squareness = widgetSize ? computeSquarenessScore(widgetSize.w, widgetSize.h) : 1.0;

    // Top-left bias: distance from top-left corner (0, 0)
    // Prefer rectangles closer to the top-left of the viewport
    const topLeftDistance = Math.sqrt(rect.x * rect.x + rect.y * rect.y);

    // Area bonus (larger rects slightly preferred)
    const area = rect.w * rect.h;

    return squareness * 1.0 + topLeftDistance * 0.5 - area * 0.1;
}

interface PlacementCandidate {
    type: 'free-space' | 'split' | 'row-insert' | 'column-insert';
    score: number;
    layout: NormalizedLayout;
    affectedLayouts?: Record<string, NormalizedLayout>;
}

/**
 * Try to place widget in free space.
 * Returns candidates sorted by score.
 */
function tryPlaceInFreeSpace(
    layouts: NormalizedLayout[],
    widgetSize: WidgetSize,
    minSize: number,
): PlacementCandidate[] {
    if (layouts.length === 0) {
        // Empty viewport - place widget at top-left corner
        const w = Math.max(widgetSize.w, minSize);
        const h = Math.max(widgetSize.h, minSize);
        const x = 0;
        const y = 0;
        if (TRACE) {
            log.trace({
                message: 'tryPlaceInFreeSpace: empty viewport, placing widget at top-left',
                layout: { x, y, w, h },
            });
        }
        return [
            {
                type: 'free-space',
                score: computeSquarenessScore(w, h),
                layout: { x, y, w, h },
            },
        ];
    }

    const { xLines, yLines } = collectGridLines(layouts);
    const grid = buildOccupancyGrid(layouts, xLines, yLines);
    const freeRects = findMaximalFreeRectangles(grid, xLines, yLines);

    if (TRACE) {
        log.trace({
            message: 'tryPlaceInFreeSpace: grid lines',
            xLines,
            yLines,
            gridRows: grid.length,
            gridCols: grid[0]?.length ?? 0,
        });
        log.trace({
            message: 'tryPlaceInFreeSpace: found free rectangles',
            freeRectCount: freeRects.length,
            freeRects: freeRects.map((r) => ({
                x: r.x.toFixed(3),
                y: r.y.toFixed(3),
                w: r.w.toFixed(3),
                h: r.h.toFixed(3),
                area: (r.w * r.h).toFixed(4),
            })),
            requestedSize: widgetSize,
            minSize,
        });
    }

    const candidates: PlacementCandidate[] = [];

    for (const rect of freeRects) {
        // Check if free rect is large enough to be usable (at least minSize in both dimensions)
        if (rect.w < minSize || rect.h < minSize) {
            if (TRACE) {
                log.trace({
                    message: 'tryPlaceInFreeSpace: skipping rect smaller than minSize',
                    rect,
                    minSize,
                });
            }
            continue;
        }

        // Place widget in the free rect at top-left corner, using the smaller of requested size or rect size
        const w = Math.min(widgetSize.w, rect.w);
        const h = Math.min(widgetSize.h, rect.h);
        const x = rect.x;
        const y = rect.y;

        candidates.push({
            type: 'free-space',
            score: scoreFreeRect(rect, { w, h }),
            layout: { x, y, w, h },
        });
    }

    if (TRACE) {
        log.trace({
            message: 'tryPlaceInFreeSpace: generated candidates',
            candidateCount: candidates.length,
            candidates: candidates.map((c) => ({
                score: c.score.toFixed(3),
                layout: {
                    x: c.layout.x.toFixed(3),
                    y: c.layout.y.toFixed(3),
                    w: c.layout.w.toFixed(3),
                    h: c.layout.h.toFixed(3),
                },
            })),
        });
    }

    return candidates.sort((a, b) => a.score - b.score);
}

/**
 * Compute 2-way split placement candidates for a single widget.
 */
function computeSplitCandidates(
    targetId: string,
    targetLayout: NormalizedLayout,
    widgetSize: WidgetSize,
    horizontalBias: number,
    minSize: number,
): PlacementCandidate[] {
    const candidates: PlacementCandidate[] = [];
    const { x, y, w, h } = targetLayout;

    // Vertical splits (top/bottom) - widget goes above or below
    const halfH = h / 2;
    if (halfH >= minSize) {
        // Top: new widget at top, target moves to bottom
        const newWidgetTop: NormalizedLayout = { x, y, w, h: halfH };
        const targetAfterTop: NormalizedLayout = { x, y: y + halfH, w, h: halfH };
        const avgSquarenessTop =
            (computeSquarenessScore(newWidgetTop.w, newWidgetTop.h) +
                computeSquarenessScore(targetAfterTop.w, targetAfterTop.h)) /
            2;

        candidates.push({
            type: 'split',
            score: avgSquarenessTop - w * h * 5.0,
            layout: newWidgetTop,
            affectedLayouts: { [targetId]: targetAfterTop },
        });

        // Bottom: new widget at bottom, target stays at top
        const newWidgetBottom: NormalizedLayout = { x, y: y + halfH, w, h: halfH };
        const targetAfterBottom: NormalizedLayout = { x, y, w, h: halfH };
        const avgSquarenessBottom =
            (computeSquarenessScore(newWidgetBottom.w, newWidgetBottom.h) +
                computeSquarenessScore(targetAfterBottom.w, targetAfterBottom.h)) /
            2;

        candidates.push({
            type: 'split',
            score: avgSquarenessBottom - w * h * 5.0,
            layout: newWidgetBottom,
            affectedLayouts: { [targetId]: targetAfterBottom },
        });
    }

    // Horizontal splits (left/right) - widget goes to left or right
    const halfW = w / 2;
    if (halfW >= minSize) {
        // Left: new widget at left, target moves to right
        const newWidgetLeft: NormalizedLayout = { x, y, w: halfW, h };
        const targetAfterLeft: NormalizedLayout = { x: x + halfW, y, w: halfW, h };
        const avgSquarenessLeft =
            (computeSquarenessScore(newWidgetLeft.w, newWidgetLeft.h) +
                computeSquarenessScore(targetAfterLeft.w, targetAfterLeft.h)) /
            2;

        candidates.push({
            type: 'split',
            score: avgSquarenessLeft * horizontalBias - w * h * 5.0,
            layout: newWidgetLeft,
            affectedLayouts: { [targetId]: targetAfterLeft },
        });

        // Right: new widget at right, target stays at left
        const newWidgetRight: NormalizedLayout = { x: x + halfW, y, w: halfW, h };
        const targetAfterRight: NormalizedLayout = { x, y, w: halfW, h };
        const avgSquarenessRight =
            (computeSquarenessScore(newWidgetRight.w, newWidgetRight.h) +
                computeSquarenessScore(targetAfterRight.w, targetAfterRight.h)) /
            2;

        candidates.push({
            type: 'split',
            score: avgSquarenessRight * horizontalBias - w * h * 5.0,
            layout: newWidgetRight,
            affectedLayouts: { [targetId]: targetAfterRight },
        });
    }

    return candidates;
}

/**
 * Compute N-way column insertion candidates.
 * Adds widget to top or bottom of existing column.
 */
function computeColumnInsertCandidates(
    column: InferredColumn,
    layouts: Record<string, NormalizedLayout>,
    widgetSize: WidgetSize,
    horizontalBias: number,
    minSize: number,
): PlacementCandidate[] {
    const candidates: PlacementCandidate[] = [];
    const columnMembers = column.widgetIds;
    const columnWidth = column.xEnd - column.x;

    // Calculate total height of column
    let totalHeight = 0;
    for (const id of columnMembers) {
        totalHeight += layouts[id].h;
    }

    // New widget gets 1/(N+1) of total space
    const newWidgetCount = columnMembers.length + 1;
    const newWidgetHeight = totalHeight / newWidgetCount;

    // Check if resulting widgets would be too small
    if (newWidgetHeight < minSize) {
        return candidates;
    }

    const remainingHeight = totalHeight - newWidgetHeight;
    const scaleFactor = remainingHeight / totalHeight;

    // Helper to compute layouts for insertion at a position
    const computeInsertionLayouts = (
        insertAtTop: boolean,
    ): { layout: NormalizedLayout; affected: Record<string, NormalizedLayout>; avgSquareness: number } => {
        const firstMember = layouts[columnMembers[0]];
        let currentY = firstMember.y;
        const affected: Record<string, NormalizedLayout> = {};
        let newLayout: NormalizedLayout | null = null;
        let totalSquareness = 0;

        const order = insertAtTop ? ['new', ...columnMembers] : [...columnMembers, 'new'];

        for (const item of order) {
            if (item === 'new') {
                newLayout = { x: column.x, y: currentY, w: columnWidth, h: newWidgetHeight };
                totalSquareness += computeSquarenessScore(newLayout.w, newLayout.h);
                currentY += newWidgetHeight;
            } else {
                const originalH = layouts[item].h;
                const scaledH = originalH * scaleFactor;
                affected[item] = { x: column.x, y: currentY, w: columnWidth, h: scaledH };
                totalSquareness += computeSquarenessScore(columnWidth, scaledH);
                currentY += scaledH;
            }
        }

        return {
            layout: newLayout!,
            affected,
            avgSquareness: totalSquareness / newWidgetCount,
        };
    };

    // Insert at top - use new widget's area for fair comparison with splits
    const topResult = computeInsertionLayouts(true);
    const newWidgetArea = columnWidth * newWidgetHeight;
    candidates.push({
        type: 'column-insert',
        score: topResult.avgSquareness - newWidgetArea * 5.0,
        layout: topResult.layout,
        affectedLayouts: topResult.affected,
    });

    // Insert at bottom
    const bottomResult = computeInsertionLayouts(false);
    candidates.push({
        type: 'column-insert',
        score: bottomResult.avgSquareness - newWidgetArea * 5.0,
        layout: bottomResult.layout,
        affectedLayouts: bottomResult.affected,
    });

    return candidates;
}

/**
 * Compute N-way row insertion candidates.
 * Adds widget to left or right of existing row.
 */
function computeRowInsertCandidates(
    row: InferredRow,
    layouts: Record<string, NormalizedLayout>,
    widgetSize: WidgetSize,
    horizontalBias: number,
    minSize: number,
): PlacementCandidate[] {
    const candidates: PlacementCandidate[] = [];
    const rowMembers = row.widgetIds;
    const rowHeight = row.yEnd - row.y;

    // Calculate total width of row
    let totalWidth = 0;
    for (const id of rowMembers) {
        totalWidth += layouts[id].w;
    }

    // New widget gets 1/(N+1) of total space
    const newWidgetCount = rowMembers.length + 1;
    const newWidgetWidth = totalWidth / newWidgetCount;

    // Check if resulting widgets would be too small
    if (newWidgetWidth < minSize) {
        return candidates;
    }

    const remainingWidth = totalWidth - newWidgetWidth;
    const scaleFactor = remainingWidth / totalWidth;

    // Helper to compute layouts for insertion at a position
    const computeInsertionLayouts = (
        insertAtLeft: boolean,
    ): { layout: NormalizedLayout; affected: Record<string, NormalizedLayout>; avgSquareness: number } => {
        const firstMember = layouts[rowMembers[0]];
        let currentX = firstMember.x;
        const affected: Record<string, NormalizedLayout> = {};
        let newLayout: NormalizedLayout | null = null;
        let totalSquareness = 0;

        const order = insertAtLeft ? ['new', ...rowMembers] : [...rowMembers, 'new'];

        for (const item of order) {
            if (item === 'new') {
                newLayout = { x: currentX, y: row.y, w: newWidgetWidth, h: rowHeight };
                totalSquareness += computeSquarenessScore(newLayout.w, newLayout.h);
                currentX += newWidgetWidth;
            } else {
                const originalW = layouts[item].w;
                const scaledW = originalW * scaleFactor;
                affected[item] = { x: currentX, y: row.y, w: scaledW, h: rowHeight };
                totalSquareness += computeSquarenessScore(scaledW, rowHeight);
                currentX += scaledW;
            }
        }

        return {
            layout: newLayout!,
            affected,
            avgSquareness: totalSquareness / newWidgetCount,
        };
    };

    // Insert at left (horizontal operation - apply bias) - use new widget's area for fair comparison
    const leftResult = computeInsertionLayouts(true);
    const newWidgetArea = newWidgetWidth * rowHeight;
    candidates.push({
        type: 'row-insert',
        score: leftResult.avgSquareness * horizontalBias - newWidgetArea * 5.0,
        layout: leftResult.layout,
        affectedLayouts: leftResult.affected,
    });

    // Insert at right (horizontal operation - apply bias)
    const rightResult = computeInsertionLayouts(false);
    candidates.push({
        type: 'row-insert',
        score: rightResult.avgSquareness * horizontalBias - newWidgetArea * 5.0,
        layout: rightResult.layout,
        affectedLayouts: rightResult.affected,
    });

    return candidates;
}

/**
 * Evaluate all split and insertion options.
 */
function tryPlaceWithSplitOrInsert(
    layoutsRecord: Record<string, NormalizedLayout>,
    widgetSize: WidgetSize,
    horizontalBias: number,
    minSize: number,
): PlacementCandidate[] {
    const candidates: PlacementCandidate[] = [];

    // Infer columns and rows from layout
    const inferenceState = computeLayoutInferenceState(layoutsRecord);

    // Track which widgets are part of columns/rows (to avoid duplicate candidates)
    const widgetsInColumns = new Set<string>();
    const widgetsInRows = new Set<string>();

    for (const column of inferenceState.columns) {
        for (const id of column.widgetIds) {
            widgetsInColumns.add(id);
        }
    }
    for (const row of inferenceState.rows) {
        for (const id of row.widgetIds) {
            widgetsInRows.add(id);
        }
    }

    // N-way column insertions
    for (const column of inferenceState.columns) {
        candidates.push(...computeColumnInsertCandidates(column, layoutsRecord, widgetSize, horizontalBias, minSize));
    }

    // N-way row insertions
    for (const row of inferenceState.rows) {
        candidates.push(...computeRowInsertCandidates(row, layoutsRecord, widgetSize, horizontalBias, minSize));
    }

    // 2-way splits for each widget
    for (const [id, layout] of Object.entries(layoutsRecord)) {
        candidates.push(...computeSplitCandidates(id, layout, widgetSize, horizontalBias, minSize));
    }

    return candidates.sort((a, b) => a.score - b.score);
}

/**
 * Find optimal widget placement.
 *
 * @param existingLayouts - Current widget layouts keyed by container ID
 * @param widgetSize - Desired size of the new widget
 * @param options - Placement options
 * @returns The best placement result, or null if no valid placement found
 */
export function findOptimalWidgetPlacement(
    existingLayouts: Record<string, NormalizedLayout>,
    widgetSize: WidgetSize,
    options?: PlacementOptions,
): PlacementResult | null {
    const horizontalBias = options?.preferHorizontalBias ?? DEFAULT_HORIZONTAL_BIAS;
    const minSize = options?.minSize ?? DEFAULT_MIN_SIZE;

    // Clamp widget size to valid range
    const clampedSize: WidgetSize = {
        w: Math.max(Math.min(widgetSize.w, 1), minSize),
        h: Math.max(Math.min(widgetSize.h, 1), minSize),
    };

    // Convert to array for free space computation
    const layoutsArray = Object.values(existingLayouts);

    // Step 1: Try free space
    const freeSpaceCandidates = tryPlaceInFreeSpace(layoutsArray, clampedSize, minSize);
    if (freeSpaceCandidates.length > 0) {
        const best = freeSpaceCandidates[0];
        // Grow to fill small gaps to nearest obstacles
        const grownLayout = growLayoutToFillGaps(best.layout, layoutsArray);
        return {
            type: best.type,
            layout: grownLayout,
            affectedLayouts: best.affectedLayouts,
        };
    }

    // Step 2: Try splits and insertions
    const splitCandidates = tryPlaceWithSplitOrInsert(existingLayouts, clampedSize, horizontalBias, minSize);
    if (TRACE) {
        log.trace({
            message: 'findOptimalWidgetPlacement: split/insert candidates',
            candidateCount: splitCandidates.length,
            topCandidates: splitCandidates.slice(0, 5).map((c) => ({
                type: c.type,
                score: c.score.toFixed(4),
                layoutArea: (c.layout.w * c.layout.h).toFixed(4),
                affectedIds: c.affectedLayouts ? Object.keys(c.affectedLayouts) : [],
                affectedAreas: c.affectedLayouts
                    ? Object.entries(c.affectedLayouts).map(([id, l]) => ({
                          id,
                          area: (l.w * l.h).toFixed(4),
                      }))
                    : [],
            })),
        });
    }
    if (splitCandidates.length > 0) {
        const best = splitCandidates[0];
        return {
            type: best.type,
            layout: best.layout,
            affectedLayouts: best.affectedLayouts,
        };
    }

    // No valid placement found
    return null;
}
